import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { prepareData } from 'helpers/prepare-req-data'
import { prepParams } from 'helpers/prep-req-url'
import { getRespData  } from 'helpers/get-resp-data'
import client from 'api'
import _ from 'lodash'

import { v4 as uuid4 } from 'uuid'


const initialState = {
    loadTmkListStatus: 'idle',
    loadTmkListError: undefined as any,
    loadTmkListLastArgs: undefined as any,
    loadTmkStatus: 'idle',
    loadTmkError: undefined as any,
    saveTmkStatus: 'idle',
    saveTmkError: undefined as any,
    deleteTmkStatus: 'idle',
    deleteTmkError: undefined as any,

    tmkList: [] as any[],
    formItem: undefined as any,
    filterValues: {} as any,
    filterLookupValues: {} as any,

    directLoad: false,
    load: false,
    activeDivRef: null as any,

    filterValuesId: null,
    valuesFromDB: false,
    filterVisibleId: null,
    filterVisible: true,
}


export const loadTmkList = createAsyncThunk('tmkSlice/loadTmkList', async ({ parentModel, forceReload }: any, { rejectWithValue, getState }: any) => {
    const lastArgs = getState().tmkSlice.loadTmkListLastArgs
    if (_.isEqual(lastArgs, { parentModel }) && !forceReload) { return }

    let promises = []
    promises.push(client.get(
        `/api/ixweb/tmk/`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: value1.map((x: any) => ({
            ...x,
        })),
        args: { parentModel }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadTmkListStatus } = getState().tmkSlice
        return loadTmkListStatus !== 'pending'
    }
})

export const loadTmk = createAsyncThunk('tmkSlice/loadTmk', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.get(
        `/api/ixweb/tmk/?action=eql&HDR_ID=${id}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadTmkStatus } = getState().tmkSlice
        return loadTmkStatus !== 'pending'
    }
})

export const saveTmk = createAsyncThunk('tmkSlice/saveTmk', async ({ tmk }: any, { rejectWithValue }: any) => {
    let promises = []
    if (tmk?.HDR_ID) {
        promises.push(client.put(
            `/api/ixweb/tmk/${ tmk?.HDR_ID }/`,
            prepareData(tmk),
        ))
    } else {
        promises.push(client.post(
            `/api/ixweb/tmk/`,
            prepareData(tmk),
        ))
    }

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { saveTmkStatus } = getState().tmkSlice
        return saveTmkStatus !== 'pending'
    }
})

export const deleteTmk = createAsyncThunk('tmkSlice/deleteTmk', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.delete(
        `/api/ixweb/tmk/${id}/`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: id,
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { deleteTmkStatus } = getState().tmkSlice
        return deleteTmkStatus !== 'pending'
    }
})

const TmkSlice = createSlice({
    name: 'tmkSlice',
    initialState,
    reducers: {
        resetloadTmkListError: (state) => {
            state.loadTmkListError = undefined
        },
        resetloadTmk: (state) => {
            state.formItem = undefined
            state.loadTmkError = undefined
        },
        resetloadTmkError: (state) => {
            state.loadTmkError = undefined
        },
        resetsaveTmk: (state) => {
            state.formItem = undefined
            state.saveTmkError = undefined
        },
        resetsaveTmkError: (state) => {
            state.saveTmkError = undefined
        },
        resetdeleteTmkError: (state) => {
            state.deleteTmkError = undefined
        },
        removeFromTmkListById: (state, action) => {
            state.tmkList = state.tmkList.filter((x: any) => x.HDR_ID !== action.payload)
        },
        removeFromTmkListByIds: (state, action) => {
            state.tmkList = state.tmkList.filter((x: any) => action.payload.indexOf(x.HDR_ID) === -1)
        },
        setFilterValues: (state, action) => {
            if (action.payload === undefined || action.payload === null) {
                state.filterValues = {}
            } else {
                state.filterValues = { ...state.filterValues, ...action.payload }
            }
        },
        setFilterLookupValue: (state, action) => {
            const { name, value, formValue } = action.payload
            if (name) {
                state.filterLookupValues[name] = value
                state.filterValues[name] = formValue
            }
        },
        setDirectLoad: (state, action) => { state.directLoad = action.payload },
        setLoad: (state, action) => { state.load = action.payload },
        setActiveDivRef: (state, action) => { state.activeDivRef = action.payload},
        setFilterValuesId: (state, action) => {
            state.filterValuesId = action.payload
        },
        setValuesFromDB: (state, action) => {
            state.valuesFromDB = action.payload
        },
        setFilterVisibleId: (state, action) => {
            state.filterVisibleId = action.payload
        },
        setFilterVisible: (state, action) => {
            state.filterVisible = action.payload
        },
        preloadData: (state, action) => {
            state.tmkList = action.payload.map((x: any) => ({ ...x, HDR_ID: uuid4()}))
            state.loadTmkListStatus = 'preloaded'
            state.loadTmkListLastArgs = ''
        },
        clearPreloadedData: (state) => {
            state.tmkList = []
            state.loadTmkListStatus = 'idle'
            state.loadTmkListLastArgs = undefined
        },
    },
    extraReducers: builder => {
        builder.addCase(loadTmkList.pending, (state) => {
            state.loadTmkListStatus = 'pending'
            state.loadTmkListError = undefined
        })
        builder.addCase(loadTmkList.fulfilled, (state, action) => {
            state.loadTmkListStatus = 'idle'

            if (!action.payload) { return }

            state.loadTmkListLastArgs = action.payload.args

            {
                state.tmkList = action.payload.data

            }
        })
        builder.addCase(loadTmkList.rejected, (state, action) => {
            state.loadTmkListStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadTmkListError = action.error.message
            } else {
                state.loadTmkListError = action.payload
            }
        })

        builder.addCase(loadTmk.pending, (state) => {
            state.loadTmkStatus = 'pending'
            state.loadTmkError = undefined
        })
        builder.addCase(loadTmk.fulfilled, (state, action) => {
            state.loadTmkStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.tmkList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.tmkList.push(newItem) }
                else { state.tmkList[i] = { ...state.tmkList[i], ...newItem } }
            }
        })
        builder.addCase(loadTmk.rejected, (state, action) => {
            state.loadTmkStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadTmkError = action.error.message
            } else {
                state.loadTmkError = action.payload
            }
        })

        builder.addCase(saveTmk.pending, (state) => {
            state.saveTmkStatus = 'pending'
            state.saveTmkError = undefined
        })
        builder.addCase(saveTmk.fulfilled, (state, action) => {
            state.saveTmkStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.tmkList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.tmkList.push(newItem) }
                else { state.tmkList[i] = { ...state.tmkList[i], ...newItem } }
            }
        })
        builder.addCase(saveTmk.rejected, (state, action) => {
            state.saveTmkStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.saveTmkError = action.error.message
            } else {
                state.saveTmkError = action.payload
            }
        })

        builder.addCase(deleteTmk.pending, (state) => {
            state.deleteTmkStatus = 'pending'
            state.deleteTmkError = undefined
        })
        builder.addCase(deleteTmk.fulfilled, (state, action) => {
            state.deleteTmkStatus = 'idle'

            {
                if (Array.isArray(action.payload.data)) {
                    state.tmkList = state.tmkList.filter((x: any) => !action.payload.data.includes(x.HDR_ID))
                } else {
                    state.tmkList = state.tmkList.filter((x: any) => x.HDR_ID !== action.payload.data)
                }
            }
        })
        builder.addCase(deleteTmk.rejected, (state, action) => {
            state.deleteTmkStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.deleteTmkError = action.error.message
            } else {
                state.deleteTmkError = action.payload
            }
        })

    }
})

export const {
    resetloadTmkListError,
    resetloadTmk,
    resetloadTmkError,
    resetsaveTmk,
    resetsaveTmkError,
    resetdeleteTmkError,
    removeFromTmkListById,
    removeFromTmkListByIds,
    setFilterValues,
    setFilterLookupValue,
    setDirectLoad,
    setLoad,
    setActiveDivRef,
    setFilterValuesId,
    setValuesFromDB,
    setFilterVisible,
    setFilterVisibleId,
    preloadData,
    clearPreloadedData,
} = TmkSlice.actions
export default TmkSlice.reducer
